// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * ruxd's compiled report preview for Rapport mode (contract gap 4,
 * reusex-ufcm.7). Asks `GET /reports/ressourcekortlaegning/preview` once on
 * mount, then again ~1.5 s after the case changes (`revision`: a write of
 * ours, a `survey.changed` re-read, the cover, the Ressourcetabel template);
 * a 202 is polled by digest until ready or failed. The last compiled pages
 * stay in `shown` while a newer one compiles, so the tile never blanks.
 *
 * A 404 before any success means a ruxd from before the preview: `mode`
 * turns `unsupported` for good and Rapport keeps its client-composed pages.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiRequestError } from '../../api/client';
import type { ReportPreview, ReportSectionReadiness } from '../../api/types';

/** Re-request this long after the last change. */
export const PREVIEW_DEBOUNCE_MS = 1500;
/** Poll interval while compiling. */
export const PREVIEW_POLL_MS = 700;

export type PreviewMode = 'pending' | 'server' | 'unsupported';

export interface ReportPreviewState {
  mode: PreviewMode;
  /** The newest preview that has pages (or failed), kept while a newer one compiles. */
  shown: ReportPreview | null;
  /** The newest answer's sections (a 202 carries them too); null until one arrives. */
  sections: ReportSectionReadiness[] | null;
  /** A request or compile is under way (the tile says "Opdaterer…"). */
  updating: boolean;
  /** The newest compile failed (typst's message), or the request did. */
  error: string | null;
  /** Ask again now. */
  refresh: () => void;
}

export interface ReportPreviewOptions {
  ppi: number;
  templateId: number | null;
  /** Any change of identity re-requests (debounced). */
  revision: unknown;
  /** False until the case has loaded: nothing is asked before. */
  enabled: boolean;
}

export function useReportPreview({ ppi, templateId, revision, enabled }: ReportPreviewOptions): ReportPreviewState {
  const [mode, setMode] = useState<PreviewMode>('pending');
  const [shown, setShown] = useState<ReportPreview | null>(null);
  const [sections, setSections] = useState<ReportSectionReadiness[] | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modeRef = useRef(mode);
  modeRef.current = mode;
  const gen = useRef(0);
  const abort = useRef<AbortController | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const args = useRef({ ppi, templateId });
  args.current = { ppi, templateId };

  const stop = () => {
    abort.current?.abort();
    abort.current = null;
    if (pollTimer.current !== null) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  };

  const run = useCallback(() => {
    if (modeRef.current === 'unsupported') return;
    stop();
    const my = ++gen.current;
    const ctrl = new AbortController();
    abort.current = ctrl;
    const live = () => my === gen.current && !ctrl.signal.aborted;
    const { ppi: p, templateId: t } = args.current;
    setUpdating(true);

    const settle = (preview: ReportPreview): boolean => {
      setSections(preview.sections);
      if (preview.status === 'compiling') return false;
      setUpdating(false);
      if (preview.status === 'ready') {
        setShown(preview);
        setError(null);
      } else {
        setError(preview.error ?? 'Rapporten kunne ikke opbygges.');
      }
      return true;
    };

    const poll = (digest: string) => {
      pollTimer.current = setTimeout(() => {
        pollTimer.current = null;
        api
          .reportPreviewStatus(digest, p, ctrl.signal)
          .then((preview) => {
            if (!live()) return;
            if (!settle(preview)) poll(digest);
          })
          .catch((cause: unknown) => {
            if (!live()) return;
            // Superseded or evicted: ask for the current one again.
            if (cause instanceof ApiRequestError && cause.status === 404) run();
            else {
              setUpdating(false);
              setError(cause instanceof Error ? cause.message : String(cause));
            }
          });
      }, PREVIEW_POLL_MS);
    };

    api
      .reportPreview({ ppi: p, templateId: t }, ctrl.signal)
      .then(({ preview }) => {
        if (!live()) return;
        setMode('server');
        if (!settle(preview)) poll(preview.digest);
      })
      .catch((cause: unknown) => {
        if (!live()) return;
        setUpdating(false);
        if (cause instanceof ApiRequestError && cause.status === 404 && modeRef.current === 'pending') {
          setMode('unsupported');
          return;
        }
        setError(cause instanceof Error ? cause.message : String(cause));
      });
  }, []);

  // First ask as soon as the case is there; later changes are debounced.
  const asked = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (!asked.current) {
      asked.current = true;
      run();
      return;
    }
    // Say "Opdaterer…" from the change on, not only once the request leaves.
    if (modeRef.current !== 'unsupported') setUpdating(true);
    const timer = setTimeout(run, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, revision, ppi, templateId, run]);

  useEffect(
    () => () => {
      gen.current += 1;
      asked.current = false;
      stop();
    },
    [],
  );

  return { mode, shown, sections, updating, error, refresh: run };
}
